import "../css/Dashboard.css";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import AppSidebar from "../components/AppSidebar";
import ProtectedRoute from "../components/ProtectedRoute";
import ThemeToggle from "../components/ThemeToggle";

const titles: Record<string, { title: string; sub?: string }> = {
  "/dashboard": { title: "Dashboard", sub: "Overview & suggestions" },
  "/profile": { title: "My Profile" },
  "/projects": { title: "Projects" },
  "/publications": { title: "Publications" },
  "/announcements": { title: "Announcements" },
  "/messages": { title: "Messages" },
  "/matching": { title: "Matching" },
  "/auto-matching": { title: "Auto Matching" },
  "/connections": { title: "Connections" },
  "/connections/requests": { title: "Requests" },
};

export default function ProtectedLayout() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const menuRef = useRef<HTMLDivElement | null>(null);
  const mainRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setMobileMenuOpen(false);
    };

    const onClickOutside = (event: MouseEvent) => {
      if (!menuRef.current) return;
      if (!menuRef.current.contains(event.target as Node)) setMobileMenuOpen(false);
    };

    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("mousedown", onClickOutside);

    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("mousedown", onClickOutside);
    };
  }, []);

  useLayoutEffect(() => {
    const hardReset = () => {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });

      if (mainRef.current) {
        mainRef.current.scrollTop = 0;
        mainRef.current.scrollLeft = 0;
      }

      const content = document.querySelector(".content");
      if (content instanceof HTMLElement) {
        content.scrollTop = 0;
        content.scrollLeft = 0;
      }

      const active = document.activeElement as HTMLElement | null;
      if (
        active &&
        (active.tagName === "INPUT" ||
          active.tagName === "TEXTAREA" ||
          active.getAttribute("contenteditable") === "true")
      ) {
        active.blur();
      }
    };

    hardReset();
    requestAnimationFrame(hardReset);
    setTimeout(hardReset, 50);
    setTimeout(hardReset, 250);
  }, [location.pathname, location.search, location.hash]);

  const go = (path: string) => {
    setMobileMenuOpen(false);
    navigate(path);
  };

  const meta = titles[location.pathname] ?? { title: "Page" };

  return (
    <ProtectedRoute>
      <div className="dashboard-layout">
        <AppSidebar />

        <main className="dashboard-main" ref={mainRef}>
          <header className="topbar">
            <div className="topbar-left">
              <h2>{meta.title}</h2>
              {meta.sub ? <span className="topbar-sub">{meta.sub}</span> : null}
            </div>

            <div className="topbar-actions">
              <ThemeToggle />

              <div className="mobileMenu" ref={menuRef}>
                <button
                  className="mobileMenuBtn"
                  type="button"
                  onClick={() => setMobileMenuOpen((current) => !current)}
                  aria-label="Open menu"
                >
                  Menu
                </button>

                {mobileMenuOpen && (
                  <div className="mobileMenuPanel" role="menu">
                    <button type="button" onClick={() => go("/dashboard")}>
                      Dashboard
                    </button>
                    <button type="button" onClick={() => go("/profile")}>
                      My Profile
                    </button>

                    <div className="mobileMenuSep" />

                    <button type="button" onClick={() => go("/projects")}>
                      Projects
                    </button>
                    <button type="button" onClick={() => go("/publications")}>
                      Publications
                    </button>
                    <button type="button" onClick={() => go("/announcements")}>
                      Announcements
                    </button>
                    <button type="button" onClick={() => go("/messages")}>
                      Messages
                    </button>

                    <div className="mobileMenuSep" />

                    <button type="button" onClick={() => go("/matching")}>
                      Matching
                    </button>
                    <button type="button" onClick={() => go("/auto-matching")}>
                      Auto Matching
                    </button>

                    <div className="mobileMenuSep" />

                    <button type="button" onClick={() => go("/connections")}>
                      Connections
                    </button>
                    <button type="button" onClick={() => go("/connections/requests")}>
                      Requests
                    </button>
                  </div>
                )}
              </div>
            </div>
          </header>

          <section className="content">
            <Outlet />
          </section>
        </main>
      </div>
    </ProtectedRoute>
  );
}
